import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Popover, PopoverContent, PopoverTrigger, Modal, ModalContent, ModalBody } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import { copyLoginDescriptor, getLoginFromStruct, LoginDescriptor, getPrivilegeById } from '@/features/logins/loginsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import MenuItem from '@/components/Common/MenuItem';
import { NumberText } from '@/components/Common/Field';
import { DeleteModal } from '@/components/Common/Modals';

import User from './User';
import { RoleContext } from '@/app/api/common';
import { usePageSubscription } from '@/app/websocket/hooks';

const Users = () => {
  const { t } = useTranslation();

  usePageSubscription([{ type: 'user' }]);

  const logins = useAppSelector((state) => state.logins);
  const navigate = useNavigate();
  const { user } = useParams();
  const role = React.useContext(RoleContext);

  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as LoginDescriptor[]);
  const [deleteUser, setDeleteUser] = React.useState("");
  
  const requestDelete = React.useRef((id: string) => ("" as any));
  
  React.useEffect(() => {
    setCols([
    {
        field:			"username",
        headerName:		t("users:username:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    {
        field:			"role",
        headerName:		t("users:role:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },{
        field:			"privilages",
        headerName:		t("users:privileges:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
        cellRenderer:	PrivilegesCell,

        // valueGetter:	(params: any) => params.data?.privileges?.map((p: any) => p.desc || p.id).join(', ') || '',
    },
    {
        field:			"name",
        headerName:		t("users:name:caption"),
        flex:			1,
        minWidth:		200,
    },
    {
        field:			"email",
        headerName:		t("users:email:caption"),
        flex:			1,
        minWidth:		200,
    },
    // 2FA status column — only meaningful when the deployment has 2FA on.
    ...(logins.supports2FA
    ? [{
        field:			"twofa",
        headerName:		t("users:twofa:caption"),
        flex:			0,
        minWidth:		100,
        maxWidth:		120,
    }] : []),
    /*{
        field:			"owner",
        headerName:		t("users:owner:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    {
        field:			"max_projects",
        headerName:		t("users:max_projects:caption"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },*/
    /*
        field:			"password",
        headerName:		t("users:password:name"),
        flex:			1,
        minWidth:		200,
        maxWidth:		300,
    },*/
    ...(role?.admin || role?.privilages?.includes("ChangeGlobalUsers") || role?.privilages?.includes("DeleteGlobalUsers"))
    ? [{
        field:			"options",
        headerName:		t("users:options:caption"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] :[],
    ]);
    
    const rws: LoginDescriptor[] = [];
    for (const desc of logins.logins.descriptors) {
      const lgn = copyLoginDescriptor(desc);
      let lgnAny = lgn as any;

      // 2FA is "true" when the admin hasn't exempted the user (totp_disabled)
      // AND they have actually established a factor — an enrolled authenticator
      // or the email method. Anything else (exempted, or never set up) is false.
      lgnAny.twofa = String(
        !lgn.totp_disabled &&
        (lgn.totp_enrolled === true || lgn.preferred_2fa_method === 'email')
      );

      lgnAny.requestDelete = requestDelete;
      rws.push(lgn);
    }

    setRows(rws);
  }, [logins.logins.descriptors, logins.supports2FA, t]);
  
  const updateIsOpen = (val: boolean) => {
    if (!val)
      navigate(getUserPageUrl(""));
  };
  
  const processDelete = React.useCallback(async (obj: Object, answer: string) => {
    setDeleteUser("");
    
    if (answer !== "yes") {
      return;
    }
    
    if (deleteUser == "")
      return;
    
    const login = getLoginFromStruct(deleteUser, logins.logins);
    if (!login)
      return;
    
    let lgn = copyLoginDescriptor(login);
    lgn.to_delete = true;
    
    const mess = await store.setLogin(lgn);
    console.log("Users/processDelete/lgn.username=" + lgn.username + "/mess=", mess);
  }, [logins.logins.descriptors, deleteUser]);
  
  const handleCellClick = React.useCallback((evt: any) => {
    if (evt.colDef.field == "options" || evt.colDef.field == "privilages")
      return;
    if(!role.admin && !role?.privilages?.includes("ChangeGlobalUsers") && !role?.privilages?.includes("ViewGlobalUsers"))
      return
    navigate(getUserPageUrl(evt.data.username));
  }, [navigate, role]);
  
  requestDelete.current = (id: string) => setDeleteUser(id);
  
  return (
  <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('users:caption') }
      </div>
      <div>
        <NumberText>
          { logins.logins.descriptors.length }
        </NumberText>
        { ' ' +
          t('users:caption') +
        ' - '
        }
        <NumberText>
          { 4 }
        </NumberText>
        { ' ' +
          t('users:roles:roles')
        }
      </div>
      <Divider className="my-[8px]" />
      {(role.admin || role?.privilages?.includes("ChangeGlobalUsers"))&&
        <Button caption={t('users:new_user')}
                icon="add"
                className="uppercase mt-[20px]"
                onClick={() => navigate(getUserPageUrl('__new__'))} />
      }
    </FlexRow> 
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               "px-[32px]"
             ]}>
    
      <Grid rowData={rows}
            columnDefs={cols}
            filter={true}
            globalFilter={true}
            showPages={true}
            maxHeight="calc(100vh - 380px)"
            onCellClicked={handleCellClick}
            pageSize={10} />
    </FlexRow>
  {user &&
    <Modal backdrop="opaque" 
           defaultOpen={true} 
           isDismissable={false}
           onOpenChange={updateIsOpen}
           classNames={{
             backdrop: "bg-[#0000007f]",
             wrapper: "justify-end"
           }}
           hideCloseButton={true}
           radius="none"
           placement="top">
      <ModalContent className="h-full !mx-0 !my-0 w-full max-w-[690px]">
        <ModalBody className="w-full">
          <FlexRow direction="column"
                   flex={0}>
            <User />
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </Modal>
  }
  {deleteUser != "" &&
	<DeleteModal title={t('users:delete_user:title')}
	             message={t('users:delete_user:message_u', {u: deleteUser})}
	             onResult={processDelete}>
      <>
      </>
    </DeleteModal>
  }
  </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);
  const handleEdit = () => {
    setOpen(false);
    navigate(getUserPageUrl(props.data.username));
  };
  
  const handleDelete = async () => {
    setOpen(false);
    props.data.requestDelete.current(props.data.username);
  };
  
  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {(role.admin || role?.privilages?.includes("ChangeGlobalUsers")) &&
          <MenuItem icon="edit_note"
                    label={t('users:edit_user')}
                    onClick={handleEdit} />
        }
        {(role.admin || role?.privilages?.includes("DeleteGlobalUsers")) &&
        <MenuItem icon="delete"
                  color="!text-[--Warning]"
                  label={t('users:delete_user:caption')}
                  onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export function getUserPageUrl(user?: string) {
  return "/users" +
         (	user
         	?	"/" + user
         	:	""
         );
}

const PrivilegesCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logins = useAppSelector((state) => state.logins);

  const [open, setOpen] = React.useState(false);

  const getPrivilegeDesc = (privilegeId: string) => {
    const privilege = getPrivilegeById(logins.logins.privileges, privilegeId);
    return privilege ? privilege.desc : privilegeId;
  };

  return (
  <FlexRow justify="center">
    <span className="r14">
        { props.data.privileges ? props.data.privileges.length : ""}
    </span>
  {	props.data && props.data.privileges && props.data.privileges.length
  	?
  <Popover key={1}
           placement="bottom-end"
           onOpenChange={setOpen}
           radius="md"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="material-icons cursor-pointer !text-2xl !font-bold !text-[--Dark-Grey]">
        { open ? 'expand_less' : 'navigate_next' }
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[480px] r14">
        <FlexRow align="start"
                 className="max-h-[320px] overflow-auto">
          <FlexRow direction="column"
                   flex={1}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('users:privileges:privileges_desc') }
            </FlexRow>
          {props.data.privileges.map((privilegeId: string, i: number) =>
            <FlexRow key={i}
                     className="p-[10px]">
              { getPrivilegeDesc(privilegeId) }
            </FlexRow>
          )}
          </FlexRow>
        </FlexRow>
      </FlexRow>
    </PopoverContent>
  </Popover>
    :	null
  }
  </FlexRow>
  );
};

export default Users;


